// Booking Service
// Handles all booking-related API calls
import apiService from './apiService.js';
import hospitalService from './hospitalService.js';

class BookingService {
  // ============================================================================
  // BOOKING MANAGEMENT
  // ============================================================================

  async createBooking(bookingData) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings
    // Response structure: { success: true, message: "...", data: { booking_id: "...", ... } }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific booking creation endpoint
      console.log('🏥 Using hospital service to create booking');
      return await hospitalService.createBooking(bookingData);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service to create booking');
    return await apiService.createBooking(bookingData);
  }

  async getAllBookings(params = {}) {
    // Check if this is a hospital user request
    // If hospital_id is in params or user context indicates hospital, use hospital endpoint
    const isHospitalRequest = params.hospital_id ||
      (typeof window !== 'undefined' &&
        localStorage.getItem('aps_user_data') &&
        JSON.parse(localStorage.getItem('aps_user_data'))?.role === 'HOSPITAL_USER');

    if (isHospitalRequest) {
      // Use hospital-specific active bookings endpoint
      return await hospitalService.getActiveBookings(params);
    }

    // Use standard endpoint for non-hospital users
    return await apiService.getAllBookings(params);
  }

  async getBookingById(bookingId) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/details/:bookingId
    // Response structure: { success: true, message: "...", data: { booking_id: "...", ... } }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific booking details endpoint
      console.log('🏥 Using hospital service to get booking details');
      return await hospitalService.getBookingDetails(bookingId);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service to get booking details');
    return await apiService.getBookingById(bookingId);
  }

  async getBookingDetails(bookingId) {
    // Alias for getBookingById - routes to appropriate endpoint based on user role
    return await this.getBookingById(bookingId);
  }

  async findBestAmbulance(matchData) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/smart-match
    const isHospitalRequest = matchData?.hospital_id ||
      (typeof window !== 'undefined' &&
        localStorage.getItem('aps_user_data') &&
        JSON.parse(localStorage.getItem('aps_user_data'))?.role === 'HOSPITAL_USER');

    if (isHospitalRequest) {
      // Use hospital-specific smart match endpoint
      console.log('🏥 Using hospital service for smart match');
      return await hospitalService.smartMatch(matchData);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service for smart match');
    return await apiService.findBestAmbulance(matchData);
  }

  async estimateFare(pickupLocation, dropoffLocation, ambulanceType) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/estimate-fare
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific estimate fare endpoint
      console.log('🏥 Using hospital service to estimate fare');
      return await hospitalService.estimateFare(pickupLocation, dropoffLocation, ambulanceType);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service to estimate fare');
    return await apiService.estimateFare(pickupLocation, dropoffLocation, ambulanceType);
  }

  async updateBookingStatus(bookingId, statusData) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/:bookingId/status
    // Response structure: { success: true, message: "...", data: { booking_id: "...", old_status: "...", new_status: "...", updated_at: "..." } }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific booking status update endpoint
      console.log('🏥 Using hospital service to update booking status');
      return await hospitalService.updateBookingStatus(bookingId, statusData);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service to update booking status');
    return await apiService.updateBookingStatus(bookingId, statusData);
  }

  async cancelBooking(bookingId, cancelData) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/:bookingId/cancel
    // Response structure: { success: true, message: "...", data: { booking_id: "...", status: "CANCELLED", refund_amount: null, refund_status: null, cancelled_at: "..." } }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific booking cancel endpoint
      console.log('🏥 Using hospital service to cancel booking');
      return await hospitalService.cancelBooking(bookingId, cancelData);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service to cancel booking');
    return await apiService.cancelBooking(bookingId, cancelData);
  }

  async getBookingHistory(params = {}) {
    // Check if this is a hospital user request
    const isHospitalRequest = params.hospital_id ||
      (typeof window !== 'undefined' &&
        localStorage.getItem('aps_user_data') &&
        JSON.parse(localStorage.getItem('aps_user_data'))?.role === 'HOSPITAL_USER');

    if (isHospitalRequest) {
      // Use hospital-specific booking history endpoint
      return await hospitalService.getBookingHistory(params);
    }

    // Use standard endpoint for non-hospital users
    return await apiService.getBookingHistory(params);
  }

  // ============================================================================
  // BOOKING ANALYTICS
  // ============================================================================

  async getAnalytics(params = {}) {
    return await apiService.getBookingAnalytics(params);
  }

  async getBookingTrends(params = {}) {
    try {
      const response = await apiService.get('/booking/analytics/trends', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getRevenueAnalytics(params = {}) {
    try {
      const response = await apiService.get('/booking/analytics/revenue', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getPerformanceMetrics(params = {}) {
    try {
      const response = await apiService.get('/booking/analytics/performance', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING TYPES & CATEGORIES
  // ============================================================================

  async getBookingTypes() {
    try {
      const response = await apiService.get('/booking/types');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingCategories() {
    try {
      const response = await apiService.get('/booking/categories');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getEmergencyTypes() {
    try {
      const response = await apiService.get('/booking/emergency-types');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING STATUS MANAGEMENT
  // ============================================================================

  async getBookingStatuses() {
    try {
      const response = await apiService.get('/booking/statuses');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateBookingProgress(bookingId, progressData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/progress`, progressData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async completeBooking(bookingId, completionData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/complete`, completionData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING ASSIGNMENTS
  // ============================================================================

  async assignDriver(bookingId, driverId) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/assign-driver`, {
        driverId
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async assignAmbulance(bookingId, ambulanceId) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/assign-ambulance`, {
        ambulanceId
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async reassignBooking(bookingId, assignmentData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/reassign`, assignmentData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAvailableDrivers(bookingId, params = {}) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/available-drivers`, params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getAvailableAmbulances(bookingId, params = {}) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/available-ambulances`, params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING LOCATION & ROUTING
  // ============================================================================

  async updateBookingLocation(bookingId, locationData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/location`, locationData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingRoute(bookingId) {
    // Get historical route data for route playback in history screen
    // Endpoint: /v1/hospital/bookings/:booking_id/tracking/route
    // Response: { success: true, data: [{ latitude, longitude, timestamp, speed, heading }, ...] }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      console.log('🏥 Using hospital service to get booking route');
      return await hospitalService.getBookingRoute(bookingId);
    }

    // Fallback to standard API
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/route`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateBookingRoute(bookingId, routeData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/route`, routeData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getEstimatedArrival(bookingId) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/eta`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING PAYMENTS
  // ============================================================================

  async processPayment(bookingId, paymentData) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/payment`, paymentData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getPaymentStatus(bookingId) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/payment/status`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async refundBooking(bookingId, refundData) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/refund`, refundData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getPaymentHistory(bookingId) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/payment/history`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING COMMUNICATION
  // ============================================================================

  async sendBookingUpdate(bookingId, updateData) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/notify`, updateData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingMessages(bookingId) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/messages`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async sendMessage(bookingId, messageData) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/messages`, messageData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING RATINGS & FEEDBACK
  // ============================================================================

  async submitRating(bookingId, ratingData) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/rating`, ratingData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingRating(bookingId) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/rating`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async submitFeedback(bookingId, feedbackData) {
    try {
      const response = await apiService.post(`/api/booking/${bookingId}/feedback`, feedbackData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingFeedback(bookingId) {
    try {
      const response = await apiService.get(`/api/booking/${bookingId}/feedback`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING REPORTS
  // ============================================================================

  async generateBookingReport(reportType, params = {}) {
    try {
      const response = await apiService.post('/booking/reports/generate', {
        reportType,
        ...params
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingReportHistory(params = {}) {
    try {
      const response = await apiService.get('/booking/reports/history', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async downloadBookingReport(reportId) {
    try {
      const response = await apiService.get(`/api/booking/reports/${reportId}/download`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING SEARCH & FILTERS
  // ============================================================================

  async searchBookings(searchParams) {
    try {
      const response = await apiService.post('/booking/search', searchParams);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingFilters() {
    try {
      const response = await apiService.get('/booking/filters');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingStats(params = {}) {
    try {
      const response = await apiService.get('/booking/stats', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // EMERGENCY BOOKINGS
  // ============================================================================

  async createEmergencyBooking(bookingData) {
    try {
      const response = await apiService.post('/booking/emergency', bookingData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getEmergencyBookings(params = {}) {
    try {
      const response = await apiService.get('/booking/emergency', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async prioritizeEmergencyBooking(bookingId, priorityData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/priority`, priorityData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // BOOKING SCHEDULING
  // ============================================================================

  async scheduleBooking(bookingData) {
    try {
      const response = await apiService.post('/booking/schedule', bookingData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getScheduledBookings(params = {}) {
    try {
      const response = await apiService.get('/booking/scheduled', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async rescheduleBooking(bookingId, scheduleData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/reschedule`, scheduleData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async cancelScheduledBooking(bookingId, cancelData) {
    try {
      const response = await apiService.put(`/api/booking/${bookingId}/cancel-schedule`, cancelData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getLiveTrackingData(bookingId) {
    // Check user role to determine which endpoint to use
    // New endpoint: /v1/hospital/bookings/:bookingId/tracking for hospital users
    // New endpoint: /v1/fleet/bookings/:bookingId/tracking for fleet users
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    const isFleetRequest = userRole === 'FLEET_OPERATOR' ||
      userRole === 'FLEET_ADMIN' ||
      userRole === 'TENANT_ADMIN' ||
      userRole === 'fleet';

    if (isHospitalRequest) {
      // Use hospital-specific booking tracking endpoint
      console.log('🏥 Using hospital service to get booking tracking');
      return await hospitalService.getBookingTracking(bookingId);
    }

    if (isFleetRequest) {
      // Use fleet-specific booking tracking endpoint
      // Endpoint: /v1/fleet/bookings/:bookingId/tracking
      console.log('🚛 Using fleet auth service to get booking tracking');
      const authService = require('./authService').default;
      const result = await authService.fleetGetBookingTracking(bookingId);
      // Return the data in standardized format for the hook
      return result?.data || result;
    }

    // Use standard endpoint for other users
    console.log('📋 Using standard API service to get booking tracking');
    return await apiService.getLiveTrackingData(bookingId);
  }

  async getBookingStatusHistory(bookingId) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/:bookingId/status-history
    // Response structure: { success: true, message: "...", data: [{ id: "...", old_status: null, new_status: "...", changed_by: "...", reason: null, created_at: "..." }] }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific booking status history endpoint
      console.log('🏥 Using hospital service to get booking status history');
      return await hospitalService.getBookingStatusHistory(bookingId);
    }

    // Use standard endpoint for non-hospital users
    console.log('📋 Using standard API service to get booking status history');
    return await apiService.getBookingStatusHistory(bookingId);
  }

  async getBookingTrackingEvents(bookingId) {
    // Check if this is a hospital user request
    // New endpoint: /v1/hospital/bookings/:bookingId/tracking/events
    // Response structure: { success: true, message: "...", data: [{ id, status, source, latitude, longitude, accuracy, notes, driver_name, timestamp }] }
    const userData = typeof window !== 'undefined' && localStorage.getItem('aps_user_data');
    const userRole = userData ? JSON.parse(userData)?.role : null;
    const isHospitalRequest = userRole === 'HOSPITAL_USER' ||
      userRole === 'HOSPITAL_ADMIN' ||
      userRole === 'hospital';

    if (isHospitalRequest) {
      // Use hospital-specific booking tracking events endpoint
      console.log('🏥 Using hospital service to get booking tracking events');
      return await hospitalService.getBookingTrackingEvents(bookingId);
    }

    // For non-hospital users, return empty array or use standard endpoint if available
    console.log('📋 Tracking events endpoint not available for non-hospital users');
    return { success: true, data: [] };
  }
}

// Create and export singleton instance
const bookingService = new BookingService();
export default bookingService;
